import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import type { RSVPResponse, RegistryEvent } from '../types/registry';

interface Props {
  registryId: string;
}

export default function RSVPTable({ registryId }: Props) {
  const [responses, setResponses] = useState<RSVPResponse[]>([]);
  const [events, setEvents] = useState<RegistryEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEvents();
  }, [registryId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const { data, error: eventsError } = await supabase
        .from('registry_events')
        .select('*')
        .eq('registry_id', registryId)
        .order('date', { ascending: true });

      if (eventsError) throw eventsError;
      if (data && data.length > 0) {
        setEvents(data);
        setSelectedEvent(data[0].id);
        await loadResponses(data[0].id);
      } else {
        setEvents([]);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events');
      setLoading(false);
    }
  };

  const loadResponses = async (eventId: string) => {
    try {
      setLoading(true);
      const { data, error: rsvpError } = await supabase
        .from('registry_rsvps')
        .select('*')
        .eq('registry_id', registryId)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (rsvpError) throw rsvpError;
      setResponses(data || []);
    } catch (err) {
      console.error('Error loading RSVPs:', err);
      setError('Failed to load RSVP responses');
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (eventId: string) => {
    setSelectedEvent(eventId);
    loadResponses(eventId);
  };

  const exportToExcel = () => {
    const currentEvent = events.find(e => e.id === selectedEvent);
    
    // Prepare data for export
    const exportData = responses.map(response => ({
      'Guest Name': response.name,
      'Email': response.email,
      'Phone': response.phone || '-',
      'Number of Guests': response.number_of_guests,
      'Status': response.status === 'attending' ? 'Attending' : 'Not Attending',
      'Meal Preference': response.meal_preference || '-',
      'Dietary Restrictions': response.dietary_restrictions || '-',
      'Plus One': response.plus_one ? 'Yes' : 'No',
      'Plus One Name': response.plus_one_name || '-',
      'Comments': response.comments || '-',
      'Date Responded': new Date(response.created_at).toLocaleDateString()
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RSVP Responses');

    // Save file
    XLSX.writeFile(wb, `RSVP_Responses_${currentEvent?.name || 'Event'}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-800 font-poppins">RSVP Responses</h2>
        <p className="text-gray-500 py-4">No events have been added yet. Add events to start collecting RSVPs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-gray-800 font-poppins">RSVP Responses</h2>
          {events.length > 1 && (
            <select
              value={selectedEvent}
              onChange={(e) => handleEventChange(e.target.value)}
              className="input-pastel"
            >
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.name} - {new Date(event.date).toLocaleDateString()}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={exportToExcel}
          className="flex items-center space-x-2 px-4 py-2 btn-pastel text-white rounded-md transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Export to Excel</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-pastel-purple-200">
              <th className="py-3 px-4 text-gray-600 font-medium">Guest Name</th>
              <th className="py-3 px-4 text-gray-600 font-medium">Guests</th>
              <th className="py-3 px-4 text-gray-600 font-medium">Plus One</th>
              <th className="py-3 px-4 text-gray-600 font-medium">Status</th>
              <th className="py-3 px-4 text-gray-600 font-medium">Dietary Requirements</th>
              <th className="py-3 px-4 text-gray-600 font-medium">Date Responded</th>
            </tr>
          </thead>
          <tbody>
            {responses.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-500">
                  No RSVP responses yet
                </td>
              </tr>
            ) : (
              responses.map((response) => (
                <tr key={response.id} className="border-b border-white/40 hover:bg-white/60">
                  <td className="py-3 px-4 text-gray-800">{response.name}</td>
                  <td className="py-3 px-4 text-gray-800">{response.number_of_guests}</td>
                  <td className="py-3 px-4 text-gray-800">
                    {response.plus_one ? (
                      <span className="text-gray-600">{response.plus_one_name || 'Yes'}</span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-sm ${
                      response.status === 'attending' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {response.status === 'attending' ? 'Attending' : 'Not Attending'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {response.dietary_restrictions || '-'}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {new Date(response.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
